<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { type LocationQuery, type LocationQueryRaw, useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import _ from 'lodash'

import type { CrossSectionType } from '@/stores/type/crossSection.type'
import { successState } from '@/components/calculation-loading/constants/progressState'
import { STATUS_CHOICES } from '@/components/calculation-form/constants/constants'

import { useCalculationStore } from '@/stores/calculation'
import { useProjectStore } from '@/stores/project'
import { useCrossSectionStore } from '@/stores/crossSection'
import { useCrossSectionFilterStore } from '@/stores/crossSectionFilter'
import { useMaterialStore } from '@/stores/material'

import ResultTitle from '@/components/calculation-result/ResultTitle.vue'
import CrossSectionTable from '@/components/calculation-result/CrossSectionTable.vue'
import CalculationLoading from '@/components/calculation-loading/CalculationLoading.vue'

const route = useRoute()
const router = useRouter()

const calculationStore = useCalculationStore()
const projectStore = useProjectStore()
const crossSectionStore = useCrossSectionStore()
const crossSectionFilterStore = useCrossSectionFilterStore()
const materialStore = useMaterialStore()

const { currentItem } = storeToRefs(calculationStore)
const { items: crossSectionItems, fields } = storeToRefs(crossSectionStore)

const { initBaseCrossSectionFilterSet, baseCrossSectionFilterSet } =
  storeToRefs(crossSectionFilterStore)

const calculationStatus = ref<string>('')

const calculationId = computed(() => parseInt(route.params.calculationId as string))

const allItems = computed(() => {
  const items = [...crossSectionItems.value]

  const sortByFavorite = (a: CrossSectionType, b: CrossSectionType) =>
    a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1
  return items.sort((a, b) => {
    if (a.favorite !== b.favorite) return sortByFavorite(a, b)
    return 0
  })
})

const initializeFilterSets = () => {
  initBaseCrossSectionFilterSet.value.calculation = calculationId.value
  baseCrossSectionFilterSet.value = { ...initBaseCrossSectionFilterSet.value }
}

const processQueryParams = (query: LocationQuery | LocationQueryRaw) => {
  const filterSets = [baseCrossSectionFilterSet.value]

  Object.entries(query).forEach(([key, value]) => {
    filterSets.forEach((filterSet) => {
      if (key in filterSet && value !== null) {
        if (value === 'true' || value === 'false') {
          filterSet[key] = value === 'true'
        } else if (typeof filterSet[key] === 'number') {
          filterSet[key] = Number(value)
        } else {
          filterSet[key] = value as string
        }
      }
    })
  })
}

const fetchFilterItems = async () => {
  crossSectionStore.resetPagination()
  await crossSectionStore.fetchPaginationItems(baseCrossSectionFilterSet.value)
}

const updateQueryParams = async () => {
  const getDifferences = (
    filterSet: Record<string, any>,
    initFilterSet: Record<string, any>
  ): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(filterSet).filter(([key, value]) => initFilterSet[key] !== value)
    )
  }

  await router.replace({
    query: {
      ...getDifferences(baseCrossSectionFilterSet.value, initBaseCrossSectionFilterSet.value)
    } as LocationQueryRaw
  })
}

const applyFilter = async () => {
  await fetchFilterItems()
  await updateQueryParams()
}

const clearAllFilter = async () => {
  crossSectionFilterStore.clearAllFilter()
  await fetchFilterItems()
  await updateQueryParams()
}

const updateCalculationStatus = async (value: string) => {
  if (value === successState) {
    await calculationStore.fetchItem(calculationId.value)
  }
  calculationStatus.value = value
}

onMounted(async () => {
  if (!currentItem.value || _.isEmpty(currentItem.value)) {
    await calculationStore.fetchOptions()
    if (calculationId.value) {
      await calculationStore.fetchItem(calculationId.value)
    }
  }
  if (currentItem.value?.project) {
    const { project, material } = currentItem.value
    await projectStore.fetchOptions()
    await projectStore.fetchItem(project)
    if (material) {
      materialStore.setCurrentItem(material)
    }
  }
  if (currentItem.value?.id) {
    await crossSectionStore.fetchOptions()
    initializeFilterSets()
    processQueryParams(route.query)
  }
})

onUnmounted(() => {
  projectStore.resetAll()
  calculationStore.resetAll()
  crossSectionStore.resetAll()
  crossSectionFilterStore.resetAll()
})
</script>

<template>
  <div
    style="display: flex; flex-direction: column"
    class="fill-height"
    v-if="currentItem?.status === STATUS_CHOICES.DONE || calculationStatus === successState"
  >
    <ResultTitle
      v-if="currentItem"
      :items-length="allItems.length"
      @apply-filter="applyFilter"
      @clear-all-filter="clearAllFilter"
    />
    <CrossSectionTable
      v-if="currentItem && fields && initBaseCrossSectionFilterSet.calculation"
      @apply-filter="applyFilter"
      :items="allItems"
    />
  </div>
  <CalculationLoading v-else @update-calculation-status="updateCalculationStatus" />
</template>

<style scoped></style>
